import { useApolloClient, useMutation, useQuery } from '@apollo/client/react';
import { STANDALONE_ADVISORS_PATHS } from '@src/components/sco/Advisors/paths';
import { type ClarityAdvisorImportHoldPhase } from '@src/components/sco/Advisors/sections/clarityAdvisorImportHold';
import { adviseTopicsToCategoryKeys } from '@src/components/sco/Advisors/utils/categoryKeys';
import { CLAIM_CLARITY_ADVISOR_IMPORT } from '@src/gql/mutations/ClaimClarityAdvisorImport';
import { EXTRACT_ADVISOR_APPLICATION_FROM_BIO } from '@src/gql/mutations/ExtractAdvisorApplicationFromBio';
import { UPSERT_ADVISOR_PROFILE } from '@src/gql/mutations/UpsertAdvisorProfile';
import { ADVISOR_APPLY_USER_PROFILE } from '@src/gql/queries/AdvisorApplyUserProfile';
import { CLARITY_ADVISOR_IMPORT } from '@src/gql/queries/ClarityAdvisorImport';
import { MY_ADVISOR_PROFILE } from '@src/gql/queries/MyAdvisorProfile';
import { ClaimClarityAdvisorImportInput, ExtractAdvisorApplicationFromBioInput, Mutation, Query, ScoClarityAdvisorImportClaimStatus, UpsertAdvisorProfileInput } from '@src/graphql-types';
import { useCurrentUser } from '@src/hooks/useCurrentUser';
import { prettyErrorMessage } from '@src/lib/mutationErrors';
import { startupsPath } from '@src/lib/startups-url';
import { useRouter } from 'next/router';
import { useCallback, useState } from 'react';
export type { ClarityAdvisorImportHoldPhase };
const emptyList = (value?: Array<string | null> | null) => !value?.filter(Boolean).length;
export const useClarityAdvisorImport = (handle: string) => {
  const router = useRouter();
  const apolloClient = useApolloClient();
  const {
    currentUser,
    currentUserLoading,
    refetchCurrentUser
  } = useCurrentUser();
  const isRegistered = !!currentUser?.isRegistered;
  const [error, setError] = useState<string | null>(null);
  const [holdPhase, setHoldPhase] = useState<ClarityAdvisorImportHoldPhase>('idle');
  const importQuery = useQuery<Pick<Query, 'clarityAdvisorImport'>, {
    handle: string;
  }>(CLARITY_ADVISOR_IMPORT, {
    skip: !handle,
    variables: {
      handle
    }
  });
  const [claimImport, claimMutation] = useMutation<Pick<Mutation, 'claimClarityAdvisorImport'>, {
    input: ClaimClarityAdvisorImportInput;
  }>(CLAIM_CLARITY_ADVISOR_IMPORT);
  const [extractApplication, extractMutation] = useMutation<Pick<Mutation, 'extractAdvisorApplicationFromBio'>, {
    input: ExtractAdvisorApplicationFromBioInput;
  }>(EXTRACT_ADVISOR_APPLICATION_FROM_BIO);
  const [upsertProfile] = useMutation<Pick<Mutation, 'upsertAdvisorProfile'>, {
    input: UpsertAdvisorProfileInput;
  }>(UPSERT_ADVISOR_PROFILE);
  const preview = importQuery.data?.clarityAdvisorImport;
  const claimStatus = preview?.claimStatus || ScoClarityAdvisorImportClaimStatus.NotFound;
  const expert = preview?.expert || null;
  const canonicalScreenName = preview?.canonicalScreenName || null;
  const firstName = expert?.firstName?.trim() || null;
  const displayName = expert?.fullName?.trim() || firstName || handle;
  const applyHref = STANDALONE_ADVISORS_PATHS.apply;
  const becomeHref = STANDALONE_ADVISORS_PATHS.becomeAStartupAdvisor;
  const importHref = handle ? STANDALONE_ADVISORS_PATHS.becomeAnAdvisorClarity(handle) : becomeHref;
  const loginHref = startupsPath('/login', {
    returnTo: importHref
  });
  // An unknown or non-expert handle has nothing to import: callers fall back to the generic CTA (#29).
  const hasImportableExpert = !!preview && claimStatus !== ScoClarityAdvisorImportClaimStatus.NotFound && claimStatus !== ScoClarityAdvisorImportClaimStatus.NotExpert;
  const importCtaLabel = displayName ? `Import ${displayName} from Clarity.fm` : 'Import from Clarity.fm';
  const onConfirm = useCallback(async () => {
    if (!handle) {
      return;
    }
    setError(null);
    setHoldPhase('claiming');
    try {
      const claimed = await claimImport({
        variables: {
          input: {
            handle,
            confirm: true
          }
        }
      });
      const payload = claimed.data?.claimClarityAdvisorImport;
      const profile = payload?.advisorProfile;
      const bio = preview?.expert?.bio?.trim();
      if (bio) {
        setHoldPhase('extracting');
        const extracted = await extractApplication({
          variables: {
            input: {
              bio
            }
          }
        });
        const extract = extracted.data?.extractAdvisorApplicationFromBio;
        const categoryKeys = adviseTopicsToCategoryKeys(extract?.categories || []);
        const skillTagTitles = (extract?.skills || []).filter((title): title is string => !!title);
        const input: UpsertAdvisorProfileInput = {};
        if (!profile?.headline && extract?.headline) {
          input.headline = extract.headline;
        }
        if (emptyList(profile?.categoryKeys) && categoryKeys.length) {
          input.categoryKeys = categoryKeys;
        }
        if (emptyList(profile?.skillTagTitles) && skillTagTitles.length) {
          input.skillTagTitles = skillTagTitles;
        }
        if (Object.keys(input).length) {
          setHoldPhase('saving');
          await upsertProfile({
            variables: {
              input
            }
          });
        }
      }
      setHoldPhase('redirecting');
      try {
        await Promise.all([refetchCurrentUser(), apolloClient.query({
          query: MY_ADVISOR_PROFILE,
          fetchPolicy: 'network-only'
        }), apolloClient.query({
          query: ADVISOR_APPLY_USER_PROFILE,
          fetchPolicy: 'network-only'
        })]);
      } catch {
        // Apply still network-fetches; a warm-cache miss should not block the redirect.
      }
      await router.push(applyHref);
    } catch (err) {
      setHoldPhase('idle');
      setError(prettyErrorMessage(err) || 'Could not import that Clarity profile.');
    }
  }, [apolloClient, applyHref, claimImport, extractApplication, handle, preview?.expert?.bio, refetchCurrentUser, router, upsertProfile]);
  const loading = !!handle && (currentUserLoading || importQuery.loading && !preview);
  const canConfirm = isRegistered && claimStatus !== ScoClarityAdvisorImportClaimStatus.NotFound && claimStatus !== ScoClarityAdvisorImportClaimStatus.NotExpert && claimStatus !== ScoClarityAdvisorImportClaimStatus.ClaimedByMe;
  return {
    handle,
    applyHref,
    becomeHref,
    loginHref,
    importHref,
    importCtaLabel,
    hasImportableExpert,
    displayName,
    firstName,
    expert,
    claimStatus,
    canonicalScreenName,
    isRegistered,
    loading,
    queryError: importQuery.error,
    error: error || (importQuery.error && !preview ? prettyErrorMessage(importQuery.error) : null),
    holdPhase,
    claiming: holdPhase === 'claiming' || claimMutation.loading,
    extracting: holdPhase === 'extracting' || holdPhase === 'saving' || holdPhase === 'redirecting' || extractMutation.loading,
    onConfirm: canConfirm ? onConfirm : undefined
  };
};